import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { TierBadge, formatScore } from "../components/TierBadge";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowUpRight, CaretUp, CaretDown, CaretRight, ShieldCheck, Warning } from "@phosphor-icons/react";
import { HotLeadsPanel } from "../components/HotLeadsPanel";

const C = { sent: "#2563EB", opens: "#059669", clicks: "#7C3AED", bounce: "#DC2626", unsub: "#D97706" };
const fmtN = (n) => (n ?? 0).toLocaleString();

const Delta = ({ value, suffix = "pt", invert = false, testid }) => {
  if (value === null || value === undefined) return <span className="text-[11px] text-zinc-300">—</span>;
  const up = value > 0;
  const flat = Math.abs(value) < 0.05;
  const good = flat ? null : (invert ? !up : up);
  const cls = flat ? "bg-zinc-100 text-zinc-500" : good ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`}>
      {!flat && (up ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      {flat ? "flat" : `${up ? "+" : ""}${suffix === "pt" ? value.toFixed(1) : fmtN(value)}${suffix === "pt" ? "pt" : ""}`}
    </span>
  );
};

const KPI = ({ label, value, sub, delta, deltaSuffix, invert, accent, testid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{label}</div>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />
    </div>
    <div className="mt-2.5 flex items-end gap-2 flex-wrap">
      <div className="font-heading text-[26px] leading-none font-medium tracking-tight text-zinc-950">{value}</div>
      <Delta value={delta} suffix={deltaSuffix} invert={invert} testid={`${testid}-delta`} />
    </div>
    {sub && <div className="mt-1.5 text-xs text-zinc-500">{sub}</div>}
  </div>
);

const FunnelCell = ({ label, value, caption, testid, last }) => (
  <div className="flex items-center min-w-0">
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold truncate">{label}</div>
      <div className="mt-1 font-heading text-xl font-medium text-zinc-950">{value}</div>
      <div className="text-[11px] text-zinc-400 truncate">{caption}</div>
    </div>
    {!last && <CaretRight size={16} className="mx-auto text-zinc-300 shrink-0 hidden sm:block" />}
  </div>
);

const AUDIENCE_TABS = [
  { key: "role", label: "Job function", param: "role" },
  { key: "org", label: "Company type", param: "org" },
  { key: "does", label: "Capability", param: null },
  { key: "level", label: "Seniority", param: "level" },
];

export default function DashboardPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("role");

  useEffect(() => {
    setLoading(true);
    api.cmoDashboard(days).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  if (!data) {
    return (
      <div className="space-y-4">
        {[24, 120, 90, 300].map((h, i) => <div key={i} className="rounded-lg bg-zinc-100 animate-pulse" style={{ height: h }} />)}
      </div>
    );
  }

  const k = data.kpis.current, p = data.kpis.previous;
  const f = data.funnel;
  const sender = data.sender || {};
  const series = data.series.map(s => ({ ...s, label: s.date.slice(5) }));
  const audRows = data.audience?.[tab] || [];
  const maxRate = Math.max(1, ...audRows.map(r => r.rate));
  const activeTab = AUDIENCE_TABS.find(t => t.key === tab);

  return (
    <div className={`space-y-5 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight text-zinc-950">Marketing overview</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Performance for the last {data.days} days, compared with the {data.days} before.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sender.level === "healthy" && (
            <span data-testid="dash-sender-pill" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
              <ShieldCheck size={13} weight="fill" /> Deliverability healthy{typeof sender.bounce_rate === "number" ? ` · ${sender.bounce_rate}% bounce` : ""}
            </span>
          )}
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} data-testid={`dash-range-${d}`}
                className={`px-3 py-1 text-xs font-medium rounded ${days === d ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {sender.level !== "healthy" && (sender.alerts || []).length > 0 && (
        <div data-testid="dash-sender-ribbon" className={`rounded-lg border p-3 flex items-start gap-2.5 ${sender.level === "critical" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`}>
          <Warning size={16} weight="fill" className={`mt-0.5 shrink-0 ${sender.level === "critical" ? "text-rose-600" : "text-amber-600"}`} />
          <div className="text-sm text-zinc-800 min-w-0">
            {sender.alerts.map((a, i) => <p key={i} className={i > 0 ? "mt-1" : ""}>{a.message}</p>)}
          </div>
          <Link to="/sender-health" className="ml-auto shrink-0 text-xs font-medium text-zinc-700 underline hover:text-zinc-950">Open Sender health</Link>
        </div>
      )}

      <HotLeadsPanel />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KPI testid="kpi-sent" label="Emails sent" accent={C.sent} value={fmtN(k.sent)}
          delta={p.sent ? k.sent - p.sent : null} deltaSuffix="n" sub={`${fmtN(k.reached)} people reached`} />
        <KPI testid="kpi-open-rate" label="Open rate" accent={C.opens} value={`${k.open_rate}%`}
          delta={p.reached ? k.open_rate - p.open_rate : null} sub={`${fmtN(k.openers)} of ${fmtN(k.reached)} people opened`} />
        <KPI testid="kpi-click-rate" label="Click rate" accent={C.clicks} value={`${k.click_rate}%`}
          delta={p.reached ? k.click_rate - p.click_rate : null} sub={`${fmtN(k.clickers)} people clicked`} />
        <KPI testid="kpi-unsub-rate" label="Unsubscribe rate" accent={C.unsub} value={`${k.unsub_rate}%`} invert
          delta={p.sent ? k.unsub_rate - p.unsub_rate : null} sub={`${fmtN(k.unsubs)} of ${fmtN(k.reached)} people unsubscribed`} />
        <KPI testid="kpi-bounce-rate" label="Bounce rate" accent={C.bounce} value={`${k.bounce_rate}%`} invert
          delta={p.sent ? k.bounce_rate - p.bounce_rate : null} sub={`${fmtN(k.bounces)} of ${fmtN(k.sent)} sends bounced · incl. soft`} />
      </div>
      <p className="text-[11px] text-zinc-400 -mt-2 px-1" data-testid="kpi-definitions">
        Activity in the selected window: opens, clicks and unsubscribes count unique people; bounces count sends (incl. soft/temporary bounces), so one contact can bounce in several campaigns.
      </p>

      <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="dash-funnel">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <FunnelCell testid="funnel-mailable" label="Mailable contacts" value={fmtN(f.mailable)} caption="active, not suppressed" />
          <FunnelCell testid="funnel-reached" label="Reached" value={fmtN(f.reached)}
            caption={`${f.mailable ? Math.round(f.reached / f.mailable * 100) : 0}% of mailable emailed`} />
          <FunnelCell testid="funnel-engaged" label="Engaged" value={fmtN(f.engaged)}
            caption={`${f.reached ? Math.round(f.engaged / f.reached * 100) : 0}% of reached opened/clicked`} />
          <FunnelCell testid="funnel-hot" label="Hot leads" value={fmtN(f.hot)} caption="tier: hot right now" />
          <FunnelCell testid="funnel-requests" label="Content requests" value={fmtN(f.publication_requests)} caption="publication requests in period" last />
        </div>
      </div>

      {data.contact_base && (() => {
        const cb = data.contact_base;
        const v = cb.verification;
        const verified = v.deliverable + v.risky + v.undeliverable;
        const pctOf = (n) => cb.total ? Math.round(n / cb.total * 1000) / 10 : 0;
        const QUALITY = [
          { key: "deliverable", label: "Confirmed deliverable", color: "#059669", n: v.deliverable },
          { key: "risky", label: "Risky", color: "#D97706", n: v.risky },
          { key: "undeliverable", label: "Undeliverable", color: "#DC2626", n: v.undeliverable },
          { key: "unverified", label: "Unverified", color: "#A1A1AA", n: v.unverified },
        ];
        return (
          <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="dash-contact-base">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <h3 className="font-heading text-base font-medium">Contact base</h3>
                <p className="text-xs text-zinc-500 mt-0.5">Current state of the database — each contact counted once, however many events they generated in the window above.</p>
              </div>
              <Link to="/contacts" className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">All contacts <ArrowUpRight size={12} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div data-testid="cb-total">
                <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Total contacts</div>
                <div className="mt-1 font-heading text-2xl font-medium text-zinc-950">{fmtN(cb.total)}</div>
                <div className="text-[11px] text-zinc-400">{fmtN(verified)} verified ({pctOf(verified)}%)</div>
              </div>
              {QUALITY.map(q => (
                <div key={q.key} data-testid={`cb-${q.key}`}>
                  <div className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: q.color }}>{q.label}</div>
                  <div className="mt-1 font-heading text-2xl font-medium text-zinc-950">{fmtN(q.n)}</div>
                  <div className="text-[11px] text-zinc-400">{pctOf(q.n)}% of all contacts</div>
                </div>
              ))}
              <div data-testid="cb-new">
                <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Added · last {data.days}d</div>
                <div className="mt-1 flex items-end gap-1.5">
                  <span className="font-heading text-2xl font-medium text-zinc-950">{fmtN(cb.new_in_window)}</span>
                  <Delta value={cb.new_prev_window ? cb.new_in_window - cb.new_prev_window : null} suffix="n" testid="cb-new-delta" />
                </div>
                <div className="text-[11px] text-zinc-400">{fmtN(cb.new_prev_window)} the {data.days}d before</div>
              </div>
            </div>
            {cb.total > 0 && (
              <div className="mt-4">
                <div className="flex h-2.5 rounded-full overflow-hidden bg-zinc-100" data-testid="cb-quality-bar">
                  {QUALITY.filter(q => q.n > 0).map(q => (
                    <div key={q.key} style={{ width: `${q.n / cb.total * 100}%`, background: q.color }} title={`${q.label}: ${fmtN(q.n)}`} />
                  ))}
                </div>
                <div className="mt-2.5 text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="cb-status-line">
                  <span>Active <strong className="text-zinc-800">{fmtN(cb.by_status.active)}</strong></span>
                  <span>Unsubscribed <strong className="text-zinc-800">{fmtN(cb.by_status.unsubscribed)}</strong></span>
                  <span>Bounced <strong className="text-zinc-800">{fmtN(cb.by_status.bounced)}</strong></span>
                  <span>Left company <strong className="text-zinc-800">{fmtN(cb.by_status.left_company)}</strong></span>
                  <span>Suppression list <strong className="text-zinc-800">{fmtN(cb.suppressed_total)}</strong></span>
                </div>
                <p className="mt-1.5 text-[11px] text-zinc-400" data-testid="cb-status-note">
                  Current status, once per contact — differs from the bounce KPI above because soft bounces never mark a contact as bounced, and past bouncers may since have been re-classified as "left company" or rescued with a new address.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-zinc-200 bg-white p-5" data-testid="dash-trend">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="font-heading text-base font-medium">Activity · last {data.days} days</h3>
            <div className="text-[11px] text-zinc-500 flex gap-3">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: "#E4E4E7" }} /> Sent</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: C.opens }} /> Opens</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: C.clicks }} /> Clicks</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart data={series} margin={{ top: 5, right: 0, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={28} />
              <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#a1a1aa" }} axisLine={false} tickLine={false} width={34} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e4e4e7" }} />
              <Bar yAxisId="l" dataKey="sent" fill="#E4E4E7" radius={[3, 3, 0, 0]} maxBarSize={16} />
              <Line yAxisId="r" type="monotone" dataKey="opens" stroke={C.opens} strokeWidth={2} dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="clicks" stroke={C.clicks} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="dash-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base font-medium">Campaign leaderboard</h3>
            <Link to="/analytics" className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">Compare <ArrowUpRight size={12} /></Link>
          </div>
          {data.leaderboard.best.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">No campaigns sent in this window</div>
          ) : (
            <>
              <ul className="space-y-2">
                {data.leaderboard.best.map((c, i) => (
                  <li key={c.id} className="flex items-center gap-2.5" data-testid={`leaderboard-row-${i}`}>
                    <span className="w-4 text-[11px] text-zinc-400 tabular-nums shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <Link to={`/campaigns/${c.id}`} className="block text-[13px] font-medium text-zinc-900 truncate hover:underline">{c.name}</Link>
                      <div className="text-[11px] text-zinc-400">{fmtN(c.sent)} sent</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-semibold tabular-nums" style={{ color: C.opens }}>{c.open_rate}%</div>
                      <div className="text-[11px] tabular-nums" style={{ color: C.clicks }}>{c.click_rate}% click</div>
                    </div>
                  </li>
                ))}
              </ul>
              {data.leaderboard.worst.length > 0 && (
                <>
                  <div className="mt-4 mb-2 text-[11px] uppercase tracking-[0.14em] text-rose-600 font-semibold">Underperforming</div>
                  <ul className="space-y-1.5">
                    {data.leaderboard.worst.map(c => (
                      <li key={c.id} className="flex items-center justify-between gap-2 text-[13px]">
                        <Link to={`/campaigns/${c.id}`} className="truncate text-zinc-600 hover:underline">{c.name}</Link>
                        <span className="tabular-nums text-rose-600 font-medium shrink-0">{c.open_rate}%</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="dash-audience">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <div>
            <h3 className="font-heading text-base font-medium">Audience insights</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Of the people you emailed in this window, who actually engaged — use this to pick your next campaign's target.</p>
          </div>
          <div className="inline-flex rounded-md border border-zinc-200 p-0.5">
            {AUDIENCE_TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`audience-tab-${t.key}`}
                className={`px-2.5 py-1 text-xs font-medium rounded ${tab === t.key ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {audRows.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md mt-3">Not enough sends in this window to break down by {activeTab.label.toLowerCase()}.</div>
        ) : (
          <div className="mt-3 space-y-1.5">
            {audRows.map((r, i) => (
              <div key={r.tag} className="flex items-center gap-3" data-testid={`audience-row-${i}`}>
                <div className="w-44 sm:w-56 shrink-0 truncate text-[13px] text-zinc-800">
                  {activeTab.param ? (
                    <Link to={`/contacts?${activeTab.param}=${encodeURIComponent(r.name)}`} className="hover:underline" title={`View ${r.name} contacts`}>{r.name}</Link>
                  ) : r.name}
                </div>
                <div className="flex-1 h-4 rounded bg-zinc-100 overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${Math.max(2, r.rate / maxRate * 100)}%`, background: C.opens, opacity: 0.85 }} />
                </div>
                <div className="w-12 text-right text-[13px] font-semibold tabular-nums text-zinc-900 shrink-0">{r.rate}%</div>
                <div className="w-24 text-right text-[11px] text-zinc-400 tabular-nums shrink-0 hidden sm:block">{fmtN(r.reached)} emailed</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-zinc-200 bg-white p-5" data-testid="top-prospects-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-base font-medium">Top prospects</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Your most engaged contacts right now — prioritise these for follow-up.</p>
            </div>
            <Link to="/contacts?tier=hot" className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">All hot leads <ArrowUpRight size={12} /></Link>
          </div>
          {(!data.top_prospects || data.top_prospects.length === 0) ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">No engagement scored yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-2 pr-3 font-medium w-8">#</th>
                  <th className="text-left py-2 pr-3 font-medium">Contact</th>
                  <th className="text-left py-2 pr-3 font-medium">Company</th>
                  <th className="text-left py-2 pr-3 font-medium">Tier</th>
                  <th className="text-right py-2 pr-3 font-medium">Score</th>
                  <th className="text-right py-2 font-medium">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {data.top_prospects.map((pr, i) => (
                  <tr key={pr.id} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60" data-testid={`prospect-row-${i}`}>
                    <td className="py-2.5 pr-3 text-zinc-400">{i + 1}</td>
                    <td className="py-2.5 pr-3">
                      <Link to={`/contacts/${pr.id}`} className="font-medium text-zinc-900 hover:underline">{pr.first_name} {pr.last_name}</Link>
                      <div className="text-[11px] text-zinc-500">{pr.email}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-zinc-700">{pr.company || "—"}</td>
                    <td className="py-2.5 pr-3"><TierBadge score={pr.engagement_score} /></td>
                    <td className="py-2.5 pr-3 text-right font-medium text-zinc-900">{formatScore(pr.engagement_score)}</td>
                    <td className="py-2.5 text-right text-xs text-zinc-500">{pr.last_activity_at ? new Date(pr.last_activity_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="recent-campaigns-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-medium">Recent campaigns</h3>
            <Link to="/campaigns" className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>
          </div>
          {data.recent_campaigns.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">No campaigns yet</div>
          ) : (
            <ul className="space-y-2.5">
              {data.recent_campaigns.map(c => (
                <li key={c.id} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-b-0">
                  <div className="min-w-0">
                    <Link to={`/campaigns/${c.id}`} className="block text-sm font-medium text-zinc-900 truncate hover:underline">{c.name}</Link>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {c.stats?.sent ? `${fmtN(c.stats.sent)} sent · ${Math.round((c.stats.unique_opens ?? c.stats.opens ?? 0) / c.stats.sent * 100)}% open` : c.subject}
                    </div>
                  </div>
                  <StatusBadge value={c.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
